import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { formatMediaPreferencesForLLM } from 'js/src/agent/tools/media-agent-utils';
import { formatVoiceTraits } from 'js/src/agent/tools/voice-trait-format';
import type { AIVoice, ColorGradingFilter, TemplateOption, UserVideoHistory } from 'js/src/client';
import { ApiService, GlobalTemplate, MaxVideoDurationSeconds } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import {
    isUserOnFreePlan,
    MAX_FREE_DURATION_SECONDS,
} from 'js/src/components/sketch/sketch-duration-utils';
import {
    getCanUsePremiumAIVoices,
    getMaxVideoDurationSeconds,
    loadTeamSettings,
} from 'js/src/data/account';
import {
    ensureAIVoiceLoaded,
    fetchAIVoicesTastingMenu,
    getAIVoiceById,
    getAIVoiceIds,
} from 'js/src/data/ai-voices-and-avatars';
import { loadColorGradingFilterList } from 'js/src/data/color-grading/color-grading-operations';
import { getColorGradingFilters } from 'js/src/data/color-grading/color-grading-selectors';
import {
    selectMediaOptions,
    selectMusicRecommendationMode,
} from 'js/src/data/media-recommendation-preferences';
import { loadSupportedLanguages } from 'js/src/data/translations';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';

/**
 * Plan features that affect what Sketch can do for the user.
 * These are used to inform the AI about feature limitations and when to suggest upgrades.
 */
export interface SketchPlanFeatures {
    maxVideoDurationSeconds: number;
    noBranding: boolean;
    canUploadFont: boolean;
    canUploadWatermark: boolean;
    canUsePremiumAIVoices: boolean;
    planDisplayName: string;
}

/**
 * Returns the PREMIUM FEATURES section of the system prompt,
 * informing the AI about plan limitations and when to suggest upgrades.
 */
export const getPremiumFeaturesPrompt = (planFeatures: SketchPlanFeatures | null): string => {
    if (!planFeatures) {
        return '';
    }

    const restrictions: string[] = [];
    const available: string[] = [];

    const systemMaxSeconds = MaxVideoDurationSeconds.LIMIT;
    const systemMaxMinutes = Math.floor(systemMaxSeconds / 60);
    if (planFeatures.maxVideoDurationSeconds < systemMaxSeconds) {
        const maxMinutes = Math.floor(planFeatures.maxVideoDurationSeconds / 60);
        restrictions.push(
            `- **Video duration limit**: Maximum ${maxMinutes} minute${maxMinutes !== 1 ? 's' : ''} (${planFeatures.maxVideoDurationSeconds} seconds) [Lumen5 at top paid tier supports max of ${systemMaxMinutes} minutes or ${systemMaxSeconds} seconds]`,
        );
    } else {
        available.push(
            `- Maximum video duration of ${systemMaxMinutes} minutes (${systemMaxSeconds} seconds) [this is the system maximum, no upgrade possible to increase further]`,
        );
    }

    if (!planFeatures.noBranding) {
        restrictions.push('- **Lumen5 branding**: Videos include a Lumen5 branded outro scene');
    } else {
        available.push('- No Lumen5 branding on videos');
    }

    if (!planFeatures.canUploadFont) {
        restrictions.push('- **Custom uploaded fonts**: Not available on current plan');
    } else {
        available.push('- Custom fonts (users can upload their own fonts)');
    }

    if (!planFeatures.canUploadWatermark) {
        restrictions.push('- **Custom watermark/logo**: Not available on current plan');
    } else {
        available.push('- Custom watermark/logo uploads');
    }

    if (!planFeatures.canUsePremiumAIVoices) {
        restrictions.push('- **Premium AI voices**: Not available on current plan');
    } else {
        available.push('- Access to all premium AI voices');
    }

    let prompt = `## PAID FEATURES
The user is on the **${planFeatures.planDisplayName}** plan.

During editing/preview, Shutterstock media may display watermarks. These watermarks are automatically removed when the video is published/downloaded.

`;

    if (restrictions.length > 0) {
        prompt += `**Current plan limitations:**
${restrictions.join('\n')}

When the user requests a feature that is restricted on their plan, use the **${TOOL_NAMES.suggestUpgrade}** tool to show them an upgrade button.

`;
    }

    if (available.length > 0) {
        prompt += `**Paid features this user has access to on their plan:**
${available.join('\n')}

`;
    }

    return prompt;
};

function formatTemplateOptions(
    customTemplates: TemplateOption[],
    publicTemplates: TemplateOption[],
): string {
    let templateOptions = '';

    if (customTemplates.length > 0) {
        templateOptions += '### Custom Templates\n';
        templateOptions +=
            "These are branded templates created specifically for the user's account. You should use these unless the user explicitly requests a different style or indicates they want a public template.\n\n";
        for (const template of customTemplates) {
            const aspectRatiosStr = template.aspect_ratio.join(', ');
            templateOptions += `- **${template.id}** (Supports ${aspectRatiosStr})\n`;
        }
        templateOptions += '\n';
    }

    if (publicTemplates.length > 0) {
        templateOptions += '### Public Templates\n\n';
        for (const template of publicTemplates) {
            const aspectRatiosStr = template.aspect_ratio.join(', ');
            templateOptions += `- **${template.id}**: ${template.description} (Supports ${aspectRatiosStr})\n`;
        }
    }

    return templateOptions;
}

export function formatUserVideoHistory(videos: UserVideoHistory[]): string {
    if (videos.length === 0) return '';

    return videos
        .map((v) => {
            const parts = [`**${v.name}**`];
            if (v.id) parts.push(`ID: ${v.id}`);
            if (v.template_id) parts.push(`Template: ${v.template_id}`);
            if (v.blueprint_name) parts.push(`Blueprint: ${v.blueprint_name}`);
            if (v.aspect_ratio) parts.push(`Aspect ratio: ${v.aspect_ratio}`);
            if (v.duration_seconds) parts.push(`Duration: ${v.duration_seconds}s`);
            if (v.narrative_thread === 'voiceover') parts.push('Has voiceover');
            else if (v.narrative_thread === 'text') parts.push('No voiceover');
            else if (v.narrative_thread) parts.push(`Thread: ${v.narrative_thread}`);
            if (v.article_title) parts.push(`Article: ${v.article_title}`);
            return `- ${parts.join(' | ')}`;
        })
        .join('\n');
}

// The system prompt lists many voices, so languages are capped to keep the
// token cost bounded — the search_voices tool shows the full language list.
const PROMPT_LANGUAGE_CAP = 3;

/**
 * One prompt line per voice: display name for the agent to talk about, id
 * (slug) for it to pass to tools, then whatever traits the voice has.
 * Exported for tests.
 */
export function getVoiceStr(v: AIVoice): string {
    const traits = formatVoiceTraits(v, { maxLanguages: PROMPT_LANGUAGE_CAP });
    const label = `- ${v.display_name} (id: ${v.slug})`;
    return traits ? `${label}: ${traits}\n` : `${label}\n`;
}

function getAIVoiceOptions(getState: GetRootState | undefined): string {
    if (!getState) {
        return '';
    }

    const state = getState();
    const canUsePremium = getCanUsePremiumAIVoices(state);

    const allVoiceIds = getAIVoiceIds(state);

    if (!allVoiceIds || allVoiceIds.length === 0) {
        return '';
    }

    // Separate voices into private, free public, and premium public
    const privateVoices: AIVoice[] = [];
    const freePublicVoices: AIVoice[] = [];
    const premiumPublicVoices: AIVoice[] = [];

    allVoiceIds.forEach((voiceId) => {
        const voice = getAIVoiceById(state, voiceId);
        if (!voice) {
            return;
        }
        if (voice.is_deprecated) {
            return;
        }

        // Categorize voice for display
        if (voice.is_private) {
            privateVoices.push(voice);
        } else if (voice.is_paid) {
            premiumPublicVoices.push(voice);
        } else {
            freePublicVoices.push(voice);
        }
    });

    // Build output
    let voiceOptions = '';

    if (privateVoices.length > 0) {
        voiceOptions += 'Brand Voices:\n';
        voiceOptions +=
            'These are custom AI voices created specifically for your team. Use these to maintain brand consistency.\n\n';
        for (const v of privateVoices) {
            voiceOptions += getVoiceStr(v);
        }
        voiceOptions += '\n';
    }

    if (freePublicVoices.length > 0) {
        voiceOptions += 'Free AI Voices:\n';
        for (const v of freePublicVoices) {
            voiceOptions += getVoiceStr(v);
        }
        voiceOptions += '\n';
    }

    if (premiumPublicVoices.length > 0) {
        if (canUsePremium) {
            voiceOptions += 'Premium AI Voices:\n';
            for (const v of premiumPublicVoices) {
                voiceOptions += getVoiceStr(v);
            }
            voiceOptions += `\nThis is not the full list of premium AI voices — use the \`${TOOL_NAMES.searchVoices}\` tool to find other voices.\n`;
        } else {
            // Don't spend prompt tokens enumerating voices this user cannot
            // use — a one-line upsell is all the model needs.
            voiceOptions +=
                'Additional premium voices exist but require a paid plan — you CANNOT use them; if the user asks for one, suggest an upgrade.\n';
        }
    }

    return voiceOptions.trim();
}

function getPlanFeaturesFromState(getState: GetRootState | undefined): SketchPlanFeatures | null {
    if (!getState) {
        return null;
    }

    const maxDuration = getMaxVideoDurationSeconds(getState());
    return {
        maxVideoDurationSeconds: isUserOnFreePlan()
            ? Math.min(maxDuration, MAX_FREE_DURATION_SECONDS)
            : maxDuration,
        noBranding: SERVER?.USER?.capabilities?.no_branding ?? false,
        canUploadFont: SERVER?.USER?.capabilities?.custom_font ?? false,
        canUploadWatermark: SERVER?.USER?.capabilities?.custom_watermark ?? false,
        canUsePremiumAIVoices: getCanUsePremiumAIVoices(getState()),
        planDisplayName: SERVER?.USER?.plan_display_name || 'Unknown',
    };
}

export interface SketchAgentContextData {
    templateOptions: string;
    userVideoHistory: string;
    templateIds: string[];
    /** Raw template records, so surfaces can format their own template list. */
    customTemplates: TemplateOption[];
    publicTemplates: TemplateOption[];
    aiVoiceOptions: string;
    planFeatures: SketchPlanFeatures | null;
    colorGradingFilters: ColorGradingFilter[];
}

export const EMPTY_SKETCH_AGENT_CONTEXT: SketchAgentContextData = {
    templateOptions: '',
    userVideoHistory: '',
    templateIds: [],
    customTemplates: [],
    publicTemplates: [],
    aiVoiceOptions: '',
    planFeatures: null,
    colorGradingFilters: [],
};

/**
 * Loads everything a sketch-surface agent needs beyond the toolbox basics:
 * template options, AI voices, the user's recent video history, and plan
 * features. Never rejects — failures toast and resolve to empty context.
 */
export async function loadSketchAgentContext(
    dispatch: AppDispatch,
    getState: GetRootState,
): Promise<SketchAgentContextData> {
    try {
        const sketchContextPromise = ApiService.sketchContextInfoRetrieve();
        const aiVoicePromise = dispatch(fetchAIVoicesTastingMenu());
        const teamSettingsPromise = dispatch(loadTeamSettings());
        const supportedLanguagesPromise = dispatch(loadSupportedLanguages());
        const colorGradingPromise = dispatch(loadColorGradingFilterList());

        const sketchContextResponse = await sketchContextPromise;
        await aiVoicePromise;
        await teamSettingsPromise;
        await supportedLanguagesPromise;
        await colorGradingPromise;

        const selectedVoiceSlug = getState?.().sketchControls?.voiceId;
        if (selectedVoiceSlug) {
            await dispatch(ensureAIVoiceLoaded(selectedVoiceSlug));
        }

        const { user_video_history } = sketchContextResponse.data;
        // global_template is a base template with no scene designs of its own, so the agent must
        // never build on it — keep it out of every list the agent sees (ids, descriptions, aspect
        // ratios). Only shapes the agent context; the raw response is untouched.
        const isGlobal = (id: string) => id === (GlobalTemplate.GLOBAL_TEMPLATE_ID as string);
        const custom_templates = sketchContextResponse.data.custom_templates.filter(
            (t) => !isGlobal(t.id),
        );
        const public_templates = sketchContextResponse.data.public_templates.filter(
            (t) => !isGlobal(t.id),
        );

        const templateIds = [
            ...custom_templates.map((t) => t.id),
            ...public_templates.map((t) => t.id),
        ];
        const templateOptions = formatTemplateOptions(custom_templates, public_templates);
        const userVideoHistory = formatUserVideoHistory(user_video_history);
        const aiVoiceOptions = getAIVoiceOptions(getState);

        if (!aiVoiceOptions.trim()) {
            ErrorReporter.capture(new Error('AI voice options are empty after loading'), {
                level: 'warning',
                extra: {
                    voiceIdsCount: getState ? getAIVoiceIds(getState())?.length : null,
                },
            });
        }

        return {
            templateOptions,
            userVideoHistory,
            templateIds,
            customTemplates: custom_templates,
            publicTemplates: public_templates,
            aiVoiceOptions,
            planFeatures: getPlanFeaturesFromState(getState),
            colorGradingFilters: getColorGradingFilters(getState()),
        };
    } catch (error) {
        ErrorReporter.capture(error, {
            level: 'warning',
            extra: { message: 'Failed to load sketch context' },
        });
        Toast.error('Failed to load account context.');
        return { ...EMPTY_SKETCH_AGENT_CONTEXT };
    }
}

export interface SketchUserPreferencesOptions {
    /**
     * True on the toolbox surface, where the agent builds videos itself and no
     * creation tool accepts a blueprint id — the blueprint is applied by loading
     * the create-similar-to-video skill instead. The legacy sketch surface's
     * create_video tool takes blueprint_id directly.
     */
    blueprintViaSkill?: boolean;
    /**
     * A recipe the user picked for this one message. Unlike the rest of the
     * preferences it is not read from the controls state, because it steers a
     * single build rather than persisting across the conversation.
     */
    recipeName?: string | null;
}

/**
 * Wraps the collected preference lines in the <user-preferences> block the
 * agent reads, or yields nothing at all when there is no preference to state.
 */
function formatUserPreferences(preferences: string[]): string {
    if (preferences.length === 0) {
        return '';
    }
    return (
        '\n\n<user-preferences>\nThe user has set the following requirements for video creation:\n\n' +
        preferences.join('\n') +
        '\n</user-preferences>'
    );
}

/**
 * The user's sketch-controls-bar selections, formatted as a <user-preferences>
 * block for inclusion in a user message. The user's media-recommendation
 * preferences (guidance, source toggles, music) are folded into the same block
 * as text lines so the agent knows which media sources are enabled and calls the
 * right search tool.
 */
export function getSketchUserPreferences(
    getState: GetRootState | undefined,
    options?: SketchUserPreferencesOptions,
): string {
    const preferences: string[] = [];

    if (options?.recipeName) {
        preferences.push(
            `**Recipe**: ${options.recipeName} (**IMPORTANT**: the user explicitly picked this recipe. Load the \`${options.recipeName}\` skill with ${TOOL_NAMES.loadSkill} and follow it before building.)`,
        );
    }

    if (!getState) {
        return formatUserPreferences(preferences);
    }

    const state = getState();
    const controls = state.sketchControls;

    if (controls?.blueprintId) {
        preferences.push(
            options?.blueprintViaSkill
                ? `**Blueprint ID**: ${controls.blueprintId} (**IMPORTANT**: the user selected a blueprint — a reference video to imitate. Load the \`${SKILL_NAMES.createSimilarToVideo}\` skill and follow it before building.)`
                : `**Blueprint ID**: ${controls.blueprintId} (**IMPORTANT**: YOU MUST PASS THIS blueprint id to the video creation tool!)`,
        );
    }

    if (controls?.templateId) {
        preferences.push(`**Template**: ${controls.templateId}`);
    }

    if (controls?.narrativeThread) {
        preferences.push(`**Narrative Thread**: ${controls.narrativeThread}`);
    }

    if (controls?.durationSeconds) {
        preferences.push(`**Duration Target**: ${controls.durationSeconds} seconds`);
    }

    if (controls?.aspectRatio) {
        preferences.push(`**Aspect Ratio**: ${controls.aspectRatio}`);
    }

    if (controls?.voiceId) {
        preferences.push(`**AI Voice**: ${controls.voiceId}`);
    }

    preferences.push(
        formatMediaPreferencesForLLM(
            selectMediaOptions(state),
            selectMusicRecommendationMode(state),
        ),
    );

    return formatUserPreferences(preferences);
}

export interface SketchPromptOptions {
    knowledgeCutoff: string;
    currentDate: string;
    canUseAIVoiceover: boolean;
    canSuggestAnimatedExplainer: boolean;
}

/**
 * The sketch system-prompt sections that are identical between the toolbox
 * sketch surface and the legacy SketchRootAgent: request screening, feature
 * routing, background, templates, AI voices, recent history, response style,
 * and the paid-features section. Both agents compose this, then append their
 * own (differing) creation-guardrails / video-creation / saving sections.
 *
 * `creationToolRefusalNote` is the one screening line that names the agent's
 * creation tool(s), which differ between the two surfaces.
 */
export function getSharedSketchPromptSections(
    context: SketchAgentContextData,
    options: SketchPromptOptions,
    creationToolRefusalNote: string,
): string {
    const { templateOptions, userVideoHistory, aiVoiceOptions, planFeatures } = context;
    const { knowledgeCutoff, currentDate, canUseAIVoiceover, canSuggestAnimatedExplainer } =
        options;

    const dateInfo =
        knowledgeCutoff && currentDate
            ? `**Current Information:**\n- Knowledge cutoff: ${knowledgeCutoff}\n- Current date: ${currentDate}\n\n`
            : '';

    const premiumFeaturesSection = getPremiumFeaturesPrompt(planFeatures ?? null);
    const voiceoverDisabled = canUseAIVoiceover
        ? ''
        : '**Voiceover disabled:** voiceover narrative thread is disabled for this account. Only use "text" narrative thread. If the user asks for voiceover, tell them to contact their admin to enable it.';

    const animatedExplainerRoutingSection = canSuggestAnimatedExplainer
        ? `### → ANIMATED EXPLAINER / GenX (for animation, illustration, cartoon, 2D, 3D)
Call the \`${TOOL_NAMES.suggestAnimatedExplainer}\` tool ONLY when the user wants the **entire video's visual style** to be animated, illustrated, cartoon, 2D, or 3D — i.e. the imagery itself is drawn/rendered animation rather than stock photos and footage. This shows a CTA banner for our animated explainer feature, which is purpose-built for content that's hard to show with stock footage.

Examples that SHOULD trigger \`${TOOL_NAMES.suggestAnimatedExplainer}\`:
- "give the animation video on its working and application"
- "create it in animation type to train other people"
- "a 3D animated bath-time sequence in a bright colorful world"
- "make a cartoon explainer about how photosynthesis works"

**CRITICAL — the word "animation"/"animated" is NOT a trigger on its own.** Before calling, ask: what is being animated? If the answer is **text, typography, captions, icons, logos, overlays, transitions, or a general "modern/dynamic" style**, then this is a REGULAR Sketch video (those motion effects are standard in every Sketch video) — do NOT call the tool. Only call it when the *subject matter itself* would be drawn/rendered as animation (characters, illustrated scenes, cartoon worlds, 2D/3D animation).
- "modern typography animations" → regular video
- "elegant animated icons and typography" → regular video
- "modern animations, smooth transitions, and professional text overlays" → regular video

After calling the tool, briefly note the animated explainer is a better fit and offer to make a regular video instead if they prefer.

`
        : '';

    return `## CRITICAL: REQUEST SCREENING (CHECK FIRST)

**Before ANY action, check if the request should be refused. If it matches a category below:**
1. ${creationToolRefusalNote}
2. Explain what you cannot do and why
3. Suggest what you CAN do instead

**Example refusal:**
> User: "Write a blog post about sustainable fashion"
> Assistant: "I'm a video creation assistant, so I can't write blog posts or articles. However, I'd be happy to create a video about sustainable fashion trends! Would you like a short social media video or a longer explainer?"

### REFUSE: Not Video Creation
- Blog posts, articles, written content
- PowerPoint, PDF, or document creation
- Audio editing or repair (standalone)
- Social media posting or scheduling

### REFUSE: Intellectual Property
- Copyrighted music (specific songs/artists)
- Movie/TV show footage
- Celebrity likenesses or deepfakes
- Trademarked logos without permission
- Fictional characters from movies, TV, games, books, or brand mascots

### REFUSE: Inappropriate or Explicit Content
- Sexually explicit, pornographic, or erotic content
- Nudity or sexually suggestive scenarios (e.g., "hot dance", "strip", "seductive performance")
- Graphic violence, gore, or torture
- Content promoting illegal activities or substance abuse
- Hateful, discriminatory, or dehumanizing content targeting any group
- **CRITICAL: Evaluate the MEANING of the request, not just keywords.**
- **CRITICAL: Watch for evasion tactics.** If a user's prior request was refused, and their follow-up softens the language but seeks the same type of content, refuse again.

### REFUSE: Technical Limitations
- Exact duration requirements (we target, not guarantee)
- Photorealistic human generation
- Interactive/branching videos
- Voice cloning of specific people
- Frame-by-frame specifications

### REFUSE: Scale Limits
- More than 10 videos at once
- Videos longer than 5 minutes
- Bulk modifications across many existing videos

### REFUSE: False Claims About Video Persistence
- NEVER claim videos are "saved to their account" or in their "drafts"
- NEVER promise to create "preview links" or "share links" through chat
- NEVER tell users to refresh the page to see their video
- NEVER imply the video exists outside this chat session
- NEVER suggest logging into Lumen5 to find the video

---

## ROUTING: DIRECT TO OTHER FEATURES

Some requests are better handled by other parts of Lumen5. Do NOT call creation tools - instead, provide the link and clear directions. You CANNOT open features for the user - just give them the link.

### → CLIP CREATOR (for repurposing existing video)
When user has long-form video and wants to extract clips:
- "Cut this into clips", "Pull out the best moments"
- "Create clips from this webinar/interview/recording"
- "Turn this into TikToks/Reels/Shorts"
- "Extract highlights", "Repurpose for social"

**You MUST include this link:** [Open Clip Creator](/app/clip-creator/)

Example response:
> "For cutting your video into clips, use **Clip Creator** - it's built exactly for this! Click here to get started: [Open Clip Creator](/app/clip-creator/)"

### → AI MEDIA TAB (for single images, assets, or gen-AI clips)
When user wants a single image, asset, or a **gen-AI clip from a single reference image**:
- "Generate an image of...", "Create a thumbnail"
- "I need a hero image / cover image"
- "Just one visual", "Single illustration"
- Requests for thumbnails, blog headers, presentation images

**Also route here for gen-AI video clips from a SINGLE uploaded image**:
- User uploads ONE image and wants it animated or transformed into video
- "Animate this image", "Make this come alive"
- "Create a short video/clip from this photo"
- Product shots wanting brief lifestyle clips ("make an ad with a model wearing this")
- Characters/people to animate
- Static scenes wanting motion effects ("make the traffic flow", "animate the clouds")

**Note**: The AI Media tab generates clips up to 8 seconds. This is ideal for social content, short ads, and animated assets.

**Directions to include:** Open or create any video in Creator, then find the **AI Media** tab in the left panel.

Example response:
> "For generating AI video clips from your image, use the **AI Media tab** in our video editor. Open any video (or create a new one), and you'll find the AI Media tab in the left panel where you can generate AI-animated clips."

${animatedExplainerRoutingSection}---

${dateInfo}**Always respond in the same language the user is communicating in.**

Your goal is to help marketers turn ideas or source material into finished marketing videos.

## BACKGROUND ABOUT LUMEN5 VIDEOS
Lumen5 creates professional marketing videos using:

**Video Basics:**
- **Scene-based editing**: Videos are composed of individual scenes, each 2-8 seconds long
- **Stock media**: Professional photos and video clips from Shutterstock and other sources

**Narrative Threads**
Each video is built around a narrative thread, which is the mechanism that is used to tell the story of the video:
- **text**: Content appears as text on top of scenes (might contain an abstract background or media). No voiceover.
- **voiceover**: AI-generated or recorded narration with supporting visuals. Voiceover can also come from user-uploaded audio or video recordings — the system will transcribe the speech and build matching scenes automatically.

**Aspect Ratios**
Videos can be created in different aspect ratios to suit various platforms:
- **landscape** (16:9): Horizontal format, best for YouTube, websites, and traditional video platforms
- **square** (1:1): Equal width and height, an older, less commonly used format
- **vertical** (9:16): Portrait format, ideal for mobile-first platforms (TikTok, Instagram Stories, Reels)
- **fourfive** (4:5): Slightly taller than square, popular for Instagram feed posts

## TEMPLATES
Each video is based on a template that has pre-designed layouts ensuring professional visual consistency.
Each template supports specific aspect ratios - you must choose a valid combination to avoid validation errors.

${templateOptions || 'No templates available.\n'}

## AI Voices
${voiceoverDisabled}
When creating videos with voiceover narrative thread, you can specify which AI voice to use.
Refer to voices by their display name when talking to the user; pass the \`id\` when setting the voice.
NOTE: this isn't a complete list of voice options, so if the user asks to use a specific voice use it (no need to check if it's valid)
${aiVoiceOptions ? `**Available AI Voices:**\n${aiVoiceOptions}\n\n` : 'AI Voice list unavailable: do not specify unless user explicitly requests a voice or you have the voice list.\n\n'}

## YOUR RECENT VIDEO HISTORY
${userVideoHistory ? `${userVideoHistory}\n\n` : 'No recent videos.\n\n'}
${userVideoHistory ? "Use this history to understand the user's video creation preferences.\n\n" : ''}

## RESPONSE STYLE
- Be conversational and collaborative
- Ask strategic questions to understand needs better
- Provide actionable, specific recommendations
- Focus on marketing effectiveness and audience engagement
- Suggest testing different approaches when appropriate
- **Format user-facing messages using markdown**: Use bullet points, bold, headings, and other markdown formatting to make your responses clear and easy to read. Keep sentences concise (no more than 2 sentences per line) and use line breaks for readability.
- For valid video requests, be proactive: create videos based on your assumptions rather than asking many questions first. Ask clarifying questions as follow ups.
- If the user provides content/URL/file, assume they want a video and proactively make one (unless it's an out-of-scope request like "write me a blog post").
- **NEVER mention tool names to users.** Do not say "${TOOL_NAMES.createVideo}", etc. in your responses. Use user-friendly terms like "stock footage video", etc. instead.

${premiumFeaturesSection}`;
}
